// Strata API client.
//
// One axios instance, talks to payouts-backend only. Auth header is injected
// from the auth store; X-Merchant-ID from the ui store.
//
// On 401 the response interceptor calls authStore.refresh() once and retries
// the original request. If refresh fails, the request rejects with the
// original 401 and the RequireAuth guard handles the redirect.
//
// JSON codec (CLAUDE.md rule 6 — money is bigint end-to-end):
// transformRequest + transformResponse run through json-bigint so bigint
// money fields survive the wire in both directions. Default JSON.stringify
// throws on bigint; default JSON.parse silently rounds integers past 2^53.
// See @/utils/bigjson for the full rationale.
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";
import { parseJsonBig, stringifyJsonBig } from "@/utils/bigjson";
import { queryClient } from "@/queryClient";

const baseURL = import.meta.env.VITE_PAYOUTS_API_URL || "";

// Skip transformer-side JSON-ification for body types axios's default
// (or the browser) serializes itself. Matches axios's built-in skip list.
function isBinaryBody(data: unknown): boolean {
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams)
    return true;
  if (typeof Blob !== "undefined" && data instanceof Blob) return true;
  if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer)
    return true;
  return false;
}

function transformReqJsonBig(data: unknown, headers: AxiosHeaders): unknown {
  if (data == null) return data;
  if (typeof data === "string") return data;
  if (isBinaryBody(data)) return data;
  // Object / array body — needs bigint-safe JSON. json-bigint emits bigint as
  // a plain JSON integer (no "n" suffix); JSON.stringify would throw outright.
  headers.set("Content-Type", "application/json");
  return stringifyJsonBig(data);
}

// MalformedJsonResponseError is thrown by transformResJsonBig when a
// nominally-successful (2xx) response body is not parseable JSON — typically
// when a misconfigured reverse proxy or CDN intercepts the request and serves
// an HTML error page with status 200. Previously the body was passed through
// as a raw string, and any downstream `data.foo` / `data.map(...)` call would
// throw a confusing TypeError mid-flight. Throwing here turns it into a
// loud axios error that flows through apiErrorMessage and surfaces the
// underlying transport issue. AUDIT-PASS-4 M-5.
export class MalformedJsonResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MalformedJsonResponseError";
  }
}

// Exported for unit tests only. Production code wires this in via
// `transformResponse: [transformResJsonBig]` on the apiClient below.
export function transformResJsonBig(
  data: unknown,
  headers?: unknown,
  status?: number,
): unknown {
  if (typeof data !== "string" || data === "") return data;
  try {
    return parseJsonBig(data);
  } catch {
    // Parse failed. Two paths:
    //
    //  1. Non-2xx response: the body is whatever the error handler can
    //     extract (sometimes a JSON envelope from the backend, sometimes
    //     an upstream HTML page from a proxy). Pass through unchanged
    //     so apiErrorMessage's status-based fallback can render something.
    //
    //  2. 2xx response with non-JSON body: a successful HTTP status but a
    //     payload nobody downstream can handle. The success-path consumers
    //     all .map / .foreach / destructure assuming JSON; letting the raw
    //     string through means the next access throws a confusing TypeError
    //     somewhere deep in a component render. Throw a typed error so the
    //     failure is loud and routable.
    const ok = status != null && status >= 200 && status < 300;
    if (ok) {
      // Best-effort content-type sniff for the error message — the
      // failure mode is usually an HTML body from an upstream proxy.
      let ct = "";
      if (headers && typeof headers === "object") {
        const h = headers as Record<string, unknown>;
        const raw = h["content-type"] ?? h["Content-Type"];
        if (typeof raw === "string") ct = raw;
      }
      const prefix = data.slice(0, 40).replace(/\s+/g, " ").trim();
      throw new MalformedJsonResponseError(
        `Expected JSON response (status ${status}, content-type "${ct}") ` +
          `but got: ${prefix}${data.length > 40 ? "…" : ""}`,
      );
    }
    return data;
  }
}

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
  transformRequest: [transformReqJsonBig],
  transformResponse: [transformResJsonBig],
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuth.getState();
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  // Prefer the slug for X-Merchant-ID — backend resolves either form, but
  // ops logs / audit trails are easier to read with slugs than opaque ints.
  // Fall back to the int id if no slug was hydrated yet (e.g. legacy session).
  const { currentMerchantId, currentMerchantSlug } = useUI.getState();
  const merchantHeader =
    currentMerchantSlug ??
    (currentMerchantId != null ? String(currentMerchantId) : null);
  if (merchantHeader) {
    config.headers = config.headers ?? {};
    config.headers["X-Merchant-ID"] = merchantHeader;
  }
  return config;
});

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // 403 on a merchant-scoped request — the persisted currentMerchantId
    // points to a merchant the user can no longer access (membership
    // revoked, merchant suspended, or the user lost their global admin
    // grant). Evicting unsticks the user: the merchant switcher rehydrates
    // from /v1/me memberships on the next render. We only act when the
    // request actually carried X-Merchant-ID — admin endpoints like
    // /v1/merchants returning 403 for non-admins must not nuke a separate
    // valid merchant context.
    const carriedMerchantHeader =
      original?.headers != null &&
      typeof original.headers === "object" &&
      "X-Merchant-ID" in original.headers;

    // Exclusion list: endpoints that are merchant-scoped at the route
    // layer but proxy to auth-service for a SPECIFIC per-permission
    // gate (`payouts:users:read`, `payouts:grants:write`). Auth-service
    // returning 403 for those means the operator's grant doesn't have
    // that permission — NOT that they've lost merchant membership.
    // Evicting the merchant context here would crash the AddMemberModal's
    // parent ("modal disappears mid-typing, looks like a page refresh")
    // every time an operator tries to use the lookup without
    // `users:read` permission. Surface the 403 to the caller's catch
    // handler instead; the modal already renders it as an inline error.
    const url = original?.url ?? "";
    const isUsersLookupOrInvite = /\/v1\/merchants\/\d+\/users\/(lookup|invite)/.test(url);

    if (status === 403 && carriedMerchantHeader && !isUsersLookupOrInvite) {
      const ui = useUI.getState();
      if (ui.currentMerchantId != null) {
        ui.setCurrentMerchantId(null);
      }
    }

    // 403 against ANY scoped endpoint (merchant OR admin) may signal that
    // the user's role was downgraded server-side. Force-invalidate ['me']
    // so RequireAuth re-evaluates against the fresh role on the next
    // render rather than waiting up to 5 minutes for the next useMe poll
    // (audit-pass-4 H-4). Cheap: a single /v1/me fetch, dedup'd by the
    // queryClient if multiple 403s land at once.
    if (status === 403) {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    }

    // Don't try to refresh on the refresh endpoint itself, or if we already retried.
    const isRefreshCall = original?.url?.endsWith("/v1/auth/refresh");

    if (status === 401 && original && !original._retried && !isRefreshCall) {
      original._retried = true;
      const ok = await useAuth.getState().refresh();
      if (ok) {
        // Re-stamp the new bearer. The frozen `original` config still carries
        // the EXPIRED token; axios.request() does not re-fire the request
        // interceptor for an already-populated InternalAxiosRequestConfig, so
        // without this overwrite the retry sends the stale Authorization,
        // gets another 401, and the user appears logged out.
        const { accessToken } = useAuth.getState();
        if (accessToken) {
          original.headers = {
            ...(original.headers ?? {}),
            Authorization: `Bearer ${accessToken}`,
          };
        }
        // Same for X-Merchant-ID — pick up any switch that happened mid-flight.
        // Prefer slug; fall back to int id (mirrors the request interceptor).
        const ui = useUI.getState();
        const headerVal =
          ui.currentMerchantSlug ??
          (ui.currentMerchantId != null ? String(ui.currentMerchantId) : null);
        if (headerVal) {
          original.headers = {
            ...(original.headers ?? {}),
            "X-Merchant-ID": headerVal,
          };
        }
        return apiClient.request(original);
      }
    }
    return Promise.reject(error);
  },
);
